import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { showSuccess, showError } from "../../lib/toast";
import { userApi } from "../../services/api/userApi";
import { AxiosError } from "axios";
import * as yup from "yup";

interface FormData {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  dateOfBirth: Date | null;
  gender: string;
}

interface FormErrors {
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  dateOfBirth?: string;
  gender?: string;
}

const EditProfile: React.FC = () => {
  const [formData, setFormData] = useState<FormData>({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    dateOfBirth: null,
    gender: "",
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [errors, setErrors] = useState<FormErrors>({});
 console.log("errors ", errors);

  useEffect(() => {
    const fetchUserProfile = async () => {
      try {
        const response = await userApi.getProfile();
        const userData = response.data;
        console.log("userData",userData)
        setFormData({
          firstName: userData.firstName || "",
          lastName: userData.lastName || "",
          email: userData.email || "",
          phone: userData.phone || "",
          dateOfBirth: userData.dateOfBirth ? new Date(userData.dateOfBirth) : null,
          gender: userData.gender || "",
        });
      } catch (error) {
        console.error("Error fetching user profile:", error);
        showError("Failed to load profile data");
      } finally {
        setIsLoading(false);
      }
    };

    fetchUserProfile();
  }, []);

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >,
  ) => {
    const { name, value } = e.target;

      if (name === "phone") {
          // Check for invalid characters
          const invalidChars = /[A-Za-z]/.test(value);
          if (invalidChars) {
              setErrors((prev) => ({
                  ...prev,
                  [name]: "Only numbers and optional + at start are allowed",
              }));
              return; // Stop further processing
          }

          // Allow + at start, digits after
          let formattedValue = value.replace(/[^\d+]/g, "");

          // Ensure + only appears at start
          if (formattedValue.indexOf("+") > 0) {
              formattedValue = formattedValue.replace(/\+/g, "");
          }

          setFormData((prev) => ({
              ...prev,
              [name]: formattedValue,
          }));

          // Clear error if input becomes valid again
          setErrors((prev) => ({
              ...prev,
              [name]: undefined,
          }));
          return;
      }

      setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    setErrors((prev) => ({ ...prev, [name]: undefined }));
  };

  const dateInputRef = React.useRef<DatePicker>(null);

  const handleDateChange = (date: Date | null) => {
    setFormData((prev) => ({
      ...prev,
      dateOfBirth: date,
    }));
    setErrors((prev) => ({ ...prev, dateOfBirth: undefined }));
  };

  const profileSchema = yup.object({
    firstName: yup
      .string()
      .trim()
      .required("First name is required")
      .min(2, "First name must be at least 2 characters")
      .matches(/^[A-Za-z]+$/, "Only letters are allowed"),
    lastName: yup
      .string()
      .trim()
      .required("Last name is required")
      .min(2, "Last name must be at least 2 characters")
      .matches(/^[A-Za-z]+$/, "Only letters are allowed"),
    email: yup
      .string()
      .required("Email is required")
      .email("Email is invalid"),
    phone: yup
      .string()
      .optional()
      .test("is-valid-phone", "Enter a valid phone number (10–15 digits)", (val) => {
        if (!val) return true;
        const digits = val.replace(/\D/g, "");
        return digits.length === 0 || (digits.length >= 10 && digits.length <= 15);
      }),
    dateOfBirth: yup
      .date()
      .nullable()
      .max(new Date(), "Date of birth cannot be in the future"),
    gender: yup
      .string()
      .oneOf(["male", "female", ""], "Invalid gender selection"),
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    // Validate first
    try {
      await profileSchema.validate(formData, { abortEarly: false });
      setErrors({});
    } catch (validationError: any) {
      const fieldErrors: FormErrors = {};
      if (Array.isArray(validationError.inner)) {
        validationError.inner.forEach((err: any) => {
          if (err.path && !fieldErrors[err.path as keyof FormErrors]) {
            (fieldErrors as any)[err.path] = err.message;
          }
        });
      } else if (validationError.path) {
        (fieldErrors as any)[validationError.path] = validationError.message;
      }
      setErrors(fieldErrors);
      return;
    }

    setIsSubmitting(true);
    try {
      // Prepare the data to be sent to the API
      const profileData = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phone: formData.phone || null,
        dateOfBirth: formData.dateOfBirth ? formData.dateOfBirth.toISOString() : null,
        gender: formData.gender || null,
      };

      // Call the updateProfile API from userApi
      await userApi.updateProfile(profileData);

      // Show success message
      showSuccess("Profile updated successfully");
    } catch (error: unknown) {
      console.error("Error updating profile:", error);
      // Show error message to the user
      if (error instanceof AxiosError && error.response?.data?.message) {

        showError(error.response.data.error || error.response.data.message);
      } else {
        showError("Failed to update profile. Please try again.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };
  return (
    <div className="min-vh-100 d-flex flex-column">
      {/* Breadcrumb */}
      <div className="py-3">
        <div className="container">
          <nav aria-label="breadcrumb">
            <ol className="breadcrumb mb-0 font_two">
              <li className="breadcrumb-item">
                <a href="/" className="text-decoration-none text-dark">
                  Home
                </a>
              </li>
              <li className="breadcrumb-item">
                <a href="/profile" className="text-decoration-none text-dark">
                  My Profile
                </a>
              </li>
              <li
                className="breadcrumb-item active fw-bold"
                aria-current="page"
              >
                Edit Profile
              </li>
            </ol>
          </nav>
        </div>
      </div>
      {/* Main Content */}
      <div className="container flex-grow-1 d-flex flex-column flex-md-row  py-1">
        {/* Left Sidebar */}
        <div className="col-md-6 mb-2 font_two">
          <div className="container">
          <h5 className="card-title mb-2 border-b border-gray-300" style={{fontSize: 20,
                borderBottomWidth: '3px',
                borderBottomStyle: 'ridge',
                borderBottomColor: '#ffffff',
                paddingBottom: '2px',
                display: 'inline-block',
                fontWeight:'500'
          }}>Account</h5>

            <p className="text-muted small mb-0">
                <span className="fw-bold" style={{fontWeight:'500'}}> Edit Profile</span>
            </p>
            <span className="small"> 
              <Link to="/change-password" style={{color:"#212529BF"}}>Update Password</Link>
              </span>
          </div>
        </div>

        {/* Right Content */}
        <div className="col-md-6 mb-2">
          <p className="heading_text_4 font_one color_darkblur mb-2">
            Edit Profile
          </p>
          <p className="text-muted mb-3" style={{fontFamily: 'Satoshi, sans-serif'}}>
          Your profile is where your journey with art begins.
          </p>

          <div>
            <hr className="mt-0" />
          </div>

          {isLoading ? (
            <div className="bg-white p-4 rounded-3 shadow-sm text-center">
              <div className="spinner-border" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="mt-2 text-muted">Loading profile data...</p>
            </div>
          ) : (
            <form
              onSubmit={handleSubmit}
              className="bg-white p-4 rounded-3 shadow-sm"
            >
            <div className="row g-3 mb-3">
              <div className="col-md-6">
                <label htmlFor="firstName" className="form-label">
                  First Name <span className="text-danger">*</span>
                </label>
                <input
                  placeholder="Enter first name"
                  type="text"
                  className={`form-control form-control-sm custom-input ${errors.firstName ? "is-invalid" : ""}`}
                  id="firstName"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                />
                {errors.firstName && (
                  <div className="font_two invalid-feedback d-block">{errors.firstName}</div>
                )}
              </div>
              <div className="col-md-6">
                <label htmlFor="lastName" className="form-label">
                  Last Name <span className="text-danger">*</span>
                </label>
                <input
                  placeholder="Enter last name"
                  type="text"
                  className={`form-control form-control-sm custom-input ${errors.lastName ? "is-invalid" : ""}`}
                  id="lastName"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                />
                {errors.lastName && (
                  <div className="font_two invalid-feedback d-block">{errors.lastName}</div>
                )}
              </div>
              <div>
                <label htmlFor="email" className="form-label">
                  Email Address <span className="text-danger">*</span>
                </label>
                <input
                  type="email"
                  placeholder="Enter Email Address"
                  className={`form-control form-control-sm custom-input ${errors.email ? "is-invalid" : ""}`}
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  // required
                />
                {errors.email && (
                  <div className="font_two invalid-feedback d-block">{errors.email}</div>
                )}
              </div>
              <div>
                <label htmlFor="phone" className="form-label">
                  Phone Number
                </label>
                <input
                  type="tel"
                  placeholder="Enter Phone Number"
                  className={`form-control form-control-sm custom-input ${errors.phone ? "is-invalid" : ""}`}
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                />
                {errors.phone && (
                  <div className="font_two invalid-feedback d-block">{errors.phone}</div>
                )}
              </div>
            </div>

            <div className="row g-3 mb-4">
              <div className="col-md-6">
                <label htmlFor="dateOfBirth" className="form-label">
                  Date of Birth
                </label>
                <div className="input-group position-relative">
                  <DatePicker
                    ref={dateInputRef}
                    selected={formData.dateOfBirth}
                    onChange={handleDateChange}
                    className={`form-control form-control-sm custom-input ${errors.dateOfBirth ? "is-invalid" : ""}`}
                    dateFormat="dd/MM/yyyy"
                    showYearDropdown
                    dropdownMode="select"
                    maxDate={new Date()}
                    placeholderText="Select date of birth"
                    showIcon
                    toggleCalendarOnIconClick={true}
                  />
                  {errors.dateOfBirth && (
                    <div className="invalid-feedback d-block">{errors.dateOfBirth}</div>
                  )}
                  {/*<span */}
                  {/*  className="input-group-text bg-transparent border-start-0"*/}
                  {/*  style={{ cursor: 'pointer' }}*/}
                  {/*  onClick={handleCalendarClick}*/}
                  {/*>*/}
                  {/*  <svg width="20" height="19" viewBox="0 0 20 19" fill="none" xmlns="http://www.w3.org/2000/svg">*/}
                  {/*    <path fillRule="evenodd" clipRule="evenodd" d="M15 1H5V2H15V1ZM18 2C18.2652 2 18.5196 2.10536 18.7071 2.29289C18.8946 2.48043 19 2.73478 19 3V17C19 17.2652 18.8946 17.5196 18.7071 17.7071C18.5196 17.8946 18.2652 18 18 18H2C1.73478 18 1.48043 17.8946 1.29289 17.7071C1.10536 17.5196 1 17.2652 1 17V3C1 2.73478 1.10536 2.48043 1.29289 2.29289C1.48043 2.10536 1.73478 2 2 2V1C1.46957 1 0.960859 1.21071 0.585786 1.58579C0.210714 1.96086 0 2.46957 0 3V17C0 17.5304 0.210714 18.0391 0.585786 18.4142C0.960859 18.7893 1.46957 19 2 19H18C18.5304 19 19.0391 18.7893 19.4142 18.4142C19.7893 18.0391 20 17.5304 20 17V3C20 2.46957 19.7893 1.96086 19.4142 1.58579C19.0391 1.21071 18.5304 1 18 1V2Z" fill="#6c757d"/>*/}
                  {/*    <path d="M16 0.5C16 0.367392 16.0527 0.240215 16.1464 0.146447C16.2402 0.0526785 16.3674 0 16.5 0C16.6326 0 16.7598 0.0526785 16.8536 0.146447C16.9473 0.240215 17 0.367392 17 0.5V2.5C17 2.63261 16.9473 2.75979 16.8536 2.85355C16.7598 2.94732 16.6326 3 16.5 3C16.3674 3 16.2402 2.94732 16.1464 2.85355C16.0527 2.75979 16 2.63261 16 2.5V0.5ZM3 0.5C3 0.367392 3.05268 0.240215 3.14645 0.146447C3.24021 0.0526785 3.36739 0 3.5 0C3.63261 0 3.75979 0.0526785 3.85355 0.146447C3.94732 0.240215 4 0.367392 4 0.5V2.5C4 2.63261 3.94732 2.75979 3.85355 2.85355C3.75979 2.94732 3.63261 3 3.5 3C3.36739 3 3.24021 2.94732 3.14645 2.85355C3.05268 2.75979 3 2.63261 3 2.5V0.5Z" fill="#6c757d"/>*/}
                  {/*    <path fillRule="evenodd" clipRule="evenodd" d="M19 6H1V5H19V6ZM5 9H3V10H5V9ZM3 8C2.73478 8 2.48043 8.10536 2.29289 8.29289C2.10536 8.48043 2 8.73478 2 9V10C2 10.2652 2.10536 10.5196 2.29289 10.7071C2.48043 10.8946 2.73478 11 3 11H5C5.26522 11 5.51957 10.8946 5.70711 10.7071C5.89464 10.5196 6 10.2652 6 10V9C6 8.73478 5.89464 8.48043 5.70711 8.29289C5.51957 8.10536 5.26522 8 5 8H3ZM11 9H9V10H11V9ZM9 8C8.73478 8 8.48043 8.10536 8.29289 8.29289C8.10536 8.48043 8 8.73478 8 9V10C8 10.2652 8.10536 10.5196 8.29289 10.7071C8.48043 10.8946 8.73478 11 9 11H11C11.2652 11 11.5196 10.8946 11.7071 10.7071C11.8946 10.5196 12 10.2652 12 10V9C12 8.73478 11.8946 8.48043 11.7071 8.29289C11.5196 8.10536 11.2652 8 11 8H9ZM17 9H15V10H17V9ZM15 8C14.7348 8 14.4804 8.10536 14.2929 8.29289C14.1054 8.48043 14 8.73478 14 9V10C14 10.2652 14.1054 10.5196 14.2929 10.7071C14.4804 10.8946 14.7348 11 15 11H17C17.2652 11 17.5196 10.8946 17.7071 10.7071C17.8946 10.5196 18 10.2652 18 10V9C18 8.73478 17.8946 8.48043 17.7071 8.29289C17.5196 8.10536 17.2652 8 17 8H15ZM5 14H3V15H5V14ZM3 13C2.73478 13 2.48043 13.1054 2.29289 13.2929C2.10536 13.4804 2 13.7348 2 14V15C2 15.2652 2.10536 15.5196 2.29289 15.7071C2.48043 15.8946 2.73478 16 3 16H5C5.26522 16 5.51957 15.8946 5.70711 15.7071C5.89464 15.5196 6 15.2652 6 15V14C6 13.7348 5.89464 13.4804 5.70711 13.2929C5.51957 13.1054 5.26522 13 5 13H3ZM11 14H9V15H11V14ZM9 13C8.73478 13 8.48043 13.1054 8.29289 13.2929C8.10536 13.4804 8 13.7348 8 14V15C8 15.2652 8.10536 15.5196 8.29289 15.7071C8.48043 15.8946 8.73478 16 9 16H11C11.2652 16 11.5196 15.8946 11.7071 15.7071C11.8946 15.5196 12 15.2652 12 15V14C12 13.7348 11.8946 13.4804 11.7071 13.2929C11.5196 13.1054 11.2652 13 11 13H9ZM17 14H15V15H17V14ZM15 13C14.7348 13 14.4804 13.1054 14.2929 13.2929C14.1054 13.4804 14 13.7348 14 14V15C14 15.2652 14.1054 15.5196 14.2929 15.7071C14.4804 15.8946 14.7348 16 15 16H17C17.2652 16 17.5196 15.8946 17.7071 15.7071C17.8946 15.5196 18 15.2652 18 15V14C18 13.7348 17.8946 13.4804 17.7071 13.2929C17.5196 13.1054 17.2652 13 17 13H15Z" fill="#6c757d"/>*/}
                  {/*  </svg>*/}
                  {/*</span>*/}
                </div>
              </div>
              <div className="col-md-6">
                <label className="form-label d-block">Gender</label>
                <div className="d-flex flex-wrap gap-4">
                  {[
                    { value: "male", label: "Male" },
                    { value: "female", label: "Female" },
                  ].map((option) => (
                    <div key={option.value} className="form-check">
                      <input
                        className="form-check-input custom-radio"
                        type="radio"
                        name="gender"
                        id={`gender-${option.value}`}
                        value={option.value}
                        checked={formData.gender === option.value}
                        onChange={handleChange}
                      />
                      <label
                        className="form-check-label font_two"
                        htmlFor={`gender-${option.value}`}
                      >
                        {option.label}
                      </label>
                    </div>
                  ))}
                </div>
                {errors.gender && (
                  <div className="text-danger small mt-1">{errors.gender}</div>
                )}
              </div>
            </div>

            <div className="d-flex justify-content-start gap-3 pt-3 border-top">
              <button
                type="submit"
                className="btn btn-dark primary-btn rounded-pill px-4"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm me-2"
                      role="status"
                      aria-hidden="true"
                    ></span>
                    Saving...
                  </>
                ) : (
                  "Update"
                )}
              </button>
              <Link
                to="/change-password"
                className="btn font_two btn-outline-secondary rounded-pill px-4"
              >
                Change Password
              </Link>
            </div>
          </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default EditProfile;
